import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  CircleCheck,
  Scissors,
  Recycle,
  Tag,
  Layers,
  Boxes,
  Sparkles,
  Calendar,
  Lock,
  CreditCard,
} from "lucide-react";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { getCityPageBySlug } from "@/data/citiesContent";

const cityNameToSlug = (name) =>
  name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/'/g, "")
    .replace(/\s+/g, "-");

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à domicile + 2 features ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison à domicile
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Livraison de vos cartons à domicile à {city.name}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Évitez le stress des trajets en périphérie et la contrainte
            d&apos;insérer des liasses de cartons volumineuses dans un
            habitacle encombré. Nous prenons en charge la manutention et le
            transport de vos fournitures jusqu&apos;à votre adresse à
            {" "}{city.name}.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              title: "Acheminement par nos équipes",
              body: "Notre propre entreprise pilote l'intégralité du réseau de livraison en Île-de-France. Sans recours à des prestataires de messagerie externes, nous maîtrisons la ponctualité et veillons à la parfaite manipulation de vos colis.",
              Icon: Truck,
            },
            {
              title: "Préservation des fibres",
              body: "Nos camions capitonnés protègent vos produits contre l'humidité ambiante et la poussière des axes routiers. Vous réceptionnez un équipement parfaitement sec, rigide et immédiatement prêt pour l'assemblage.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Quand emballer ? Conseils d'experts (3 étapes timeline) ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Conseils d&apos;experts
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Quand faut-il lancer la mise en boîte&nbsp;? Conseils
            d&apos;experts
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Anticiper le remplissage de vos contenants est la clé pour
            aborder le jour J en toute sérénité. Nos spécialistes de la
            logistique vous recommandent d&apos;adopter le calendrier suivant.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              tag: "J-30",
              subtag: "Un mois avant",
              title: "Le tri et le hors-saison",
              body: "Commencez par isoler et emballer tout ce qui n'est pas utile au quotidien. C'est le moment de mettre en boîte la décoration, les collections de livres, les archives administratives et les vêtements de la saison inverse.",
            },
            {
              tag: "J-15",
              subtag: "Deux semaines avant",
              title: "Le cœur du logement",
              body: "Attaquez-vous aux pièces secondaires, au matériel informatique non essentiel, aux ustensiles de cuisine occasionnels et au linge de maison. Vos boîtes doivent commencer à s'empiler de manière ordonnée le long des murs.",
            },
            {
              tag: "J-3",
              subtag: "Trois jours avant",
              title: "Le quotidien",
              body: "Emballez la vaisselle courante, les derniers vêtements et les équipements de loisirs. Ne laissez de côté que le strict minimum nécessaire pour les dernières 24 heures (votre kit de survie).",
            },
          ].map(({ tag, subtag, title, body }) => (
            <div
              key={tag}
              className="relative bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center h-9 px-2.5 rounded-lg bg-[#237cb5] text-white text-[13px] font-extrabold tracking-wide">
                  {tag}
                </span>
                <span className="text-[12px] uppercase tracking-[0.12em] font-bold text-[#5b6b7a]">
                  {subtag}
                </span>
              </div>
              <h3 className="mt-3 flex items-center gap-2 font-display font-extrabold text-[15.5px] text-[#0f1d2b]">
                <Calendar className="h-4 w-4 text-[#237cb5]" strokeWidth={2.5} />
                {title}
              </h3>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 4 — Avantages de notre gamme (3 avantages) ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#FF7A00" }}>
            Nos avantages
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Les avantages de notre gamme : pourquoi adopter notre matériel&nbsp;?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Choisir nos fournitures, c&apos;est s&apos;assurer une
            tranquillité d&apos;esprit totale grâce à des standards de
            fabrication rigoureux.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              title: "Résistance mécanique supérieure",
              body: "Nos cartons possèdent une épaisseur de cannelure renforcée qui évite l'affaissement. Vous pouvez les superposer sur plusieurs niveaux dans le véhicule de transport sans redouter l'écrasement des modules inférieurs.",
              Icon: ShieldCheck,
            },
            {
              title: "Éco-conception certifiée",
              body: "Fabriqués à partir de matières premières végétales issues de filières responsables, nos contenants sont totalement recyclables et hautement réutilisables, limitant l'impact écologique de votre transition.",
              Icon: Recycle,
            },
            {
              title: "Formats standardisés",
              body: "Les dimensions de nos boîtes sont calibrées pour s'emboîter parfaitement les unes dans les autres, optimisant chaque centimètre cube disponible à l'intérieur du camion de déménagement.",
              Icon: Boxes,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#FF7A00] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-montrouge-cta-shop"
          >
            Voir tous les cartons
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/packs-cartons"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-montrouge-cta-kits"
          >
            Choisir un kit
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — Réseau 92 ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Notre réseau {city.deptCode}
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Notre réseau de déménagement et livraison de matériel dans le
            {" "}{city.deptCode}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Que vous prépariez votre départ de notre commune actuelle ou que
            vous restiez au cœur des {city.deptName}, notre service de
            transport assure des rotations quotidiennes pour acheminer vos
            kits de cartons et organiser vos transferts. Voici les principales
            villes du {city.deptCode} dans lesquelles nos équipes
            interviennent en priorité&nbsp;:
          </p>
        </div>

        <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2.5 text-[15px] md:text-base">
          {city.nearbyCities.map((name) => {
            const idSlug = cityNameToSlug(name);
            const target = getCityPageBySlug(idSlug);
            return (
              <li key={name}>
                {target ? (
                  <Link
                    to={`/carton-demenagement/${target.slug}`}
                    className="inline-flex items-center font-semibold text-[#237cb5] underline underline-offset-4 decoration-[#237cb5]/40 hover:decoration-[#237cb5] hover:text-[#0f1d2b] transition-colors"
                    data-testid={`city-nearby-${idSlug}`}
                  >
                    Carton déménagement {name}
                  </Link>
                ) : (
                  <span
                    className="inline-flex items-center font-semibold text-[#5b6b7a]"
                    data-testid={`city-nearby-${idSlug}`}
                  >
                    Carton déménagement {name}
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        <div className="mt-8">
          <Link
            to={`/cartons-demenagement/${city.deptSlug}`}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-montrouge-cta-back-dept"
          >
            <Package className="h-4 w-4" strokeWidth={2.6} />
            Voir la page {city.deptName} ({city.deptCode})
          </Link>
        </div>
      </div>
    </section>
  </>
);

export default { Sections };
